import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { getPublicDonateEvent, DonateChallenge } from '../../api/payment/donate-challenge.api';
import {
  clockSkewMs,
  countdownLabel,
  formatCa,
  needsRefresh,
  donateEventRuleSentence,
  rewardRankLabel,
} from '../../utils/donate-challenge';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';

/**
 * Trang sự kiện thử thách donate CÔNG KHAI (`/su-kien/:id`) — xem không cần đăng nhập,
 * chia sẻ được ra ngoài. Khác thẻ thử thách trên trang cá nhân creator (chỉ tóm tắt top 3):
 * đây là trang riêng cho MỘT sự kiện, đủ chỗ nêu luật chơi, phần thưởng từng hạng và bảng
 * xếp hạng đầy đủ top 20 (đang diễn ra) hoặc danh sách người thắng (đã kết thúc).
 */
const DonateEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<DonateChallenge | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [skewMs, setSkewMs] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [visible, setVisible] = useState(
    typeof document !== 'undefined' && document.visibilityState === 'visible'
  );

  const load = useCallback(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    getPublicDonateEvent(id)
      .then((c) => {
        if (!c.exists) {
          setNotFound(true);
          setEvent(null);
          return;
        }
        setEvent(c);
        setSkewMs(clockSkewMs(c.serverNow));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Làm mới 30 giây/lần khi đang diễn ra và tab đang hiện, HOẶC khi đã qua mốc mà máy chủ chưa
  // báo trạng thái mới (Scheduled qua giờ bắt đầu / Running qua giờ kết thúc) — thiếu vế sau từng
  // làm trang đứng yên ở "Bắt đầu sau Đã hết giờ" cho tới khi người dùng tự bấm tải lại trang.
  usePollWhile(
    (event?.status === 'Running' && visible) || needsRefresh(event, now + skewMs),
    load,
    30000
  );

  // Đếm ngược tại chỗ mỗi giây, không hỏi lại máy chủ liên tục.
  useEffect(() => {
    if (event?.status !== 'Scheduled' && event?.status !== 'Running') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [event?.status]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-16 text-center">
        <p className="ui-muted text-sm">Đang tải…</p>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="mx-auto flex max-w-[720px] flex-col items-center gap-3 px-4 py-16 text-center">
        <Helmet>
          <title>Không tìm thấy sự kiện · Cab</title>
        </Helmet>
        <p className="ui-heading text-lg font-semibold">Không tìm thấy sự kiện.</p>
        <Link
          to="/"
          className="mt-2 rounded-full bg-[#066bff] px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-105"
        >
          Về trang chủ
        </Link>
      </div>
    );
  }

  const isFinalized = event.status === 'Finalized';
  const countdownText =
    event.status === 'Scheduled'
      ? countdownLabel('Scheduled', new Date(event.startAt).getTime() - (now + skewMs))
      : event.status === 'Running'
      ? countdownLabel('Running', new Date(event.endAt).getTime() - (now + skewMs))
      : 'Đã kết thúc';

  const renderEntryName = (name: string, userName: string | null, isAnonymous: boolean) =>
    !isAnonymous && userName ? (
      <Link to={`/user/${userName}`} className="font-semibold text-[#066bff] hover:underline">
        {name}
      </Link>
    ) : (
      <span>{name}</span>
    );

  return (
    <div className="mx-auto max-w-[720px] px-4 py-8">
      <Helmet>
        <title>{`${event.title} · Cab`}</title>
      </Helmet>

      <div className="ui-card p-5 md:p-6">
        <div className="flex items-center gap-2">
          <Trophy className="shrink-0 text-blue-600" size={22} />
          <h1 className="ui-heading break-words text-xl font-bold">{event.title}</h1>
        </div>

        {event.description && (
          <p className="ui-muted mt-2 text-sm leading-6">{event.description}</p>
        )}

        <span className="mt-3 inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-slate-700 dark:text-blue-300">
          {countdownText}
        </span>

        <div className="ui-divider mt-4 border-t pt-4">
          <p className="ui-text text-sm leading-6">
            {donateEventRuleSentence(event.mode, event.targetAmount, event.winnerCount)}
          </p>
          <p className="ui-muted mt-1 text-sm leading-6">
            Donate ẩn danh vẫn được tính, tên sẽ được che trên bảng xếp hạng.
          </p>
        </div>

        {event.rewards?.length > 0 && (
          <div className="ui-divider mt-4 border-t pt-4">
            <h2 className="ui-heading text-sm font-semibold">Phần thưởng</h2>
            <ul className="mt-2 flex flex-col gap-3">
              {event.rewards.map((r) => (
                <li
                  key={`${r.rankFrom}-${r.rankTo}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="ui-text shrink-0 font-medium">
                    {rewardRankLabel(r.rankFrom, r.rankTo)}
                  </span>
                  <span className="text-right">
                    {r.storeItemName ? (
                      <span className="ui-text block">
                        {r.storeItemName}
                        {r.durationDays ? ` · ${r.durationDays} ngày` : ''}
                      </span>
                    ) : null}
                    {r.minedCa ? (
                      <span className="ui-muted block text-xs">
                        +{formatCa(r.minedCa)} Ca đào
                      </span>
                    ) : null}
                    {!r.storeItemName && !r.minedCa ? (
                      <span className="ui-muted">Không có</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="ui-divider mt-4 border-t pt-4">
          <h2 className="ui-heading text-sm font-semibold">
            {isFinalized ? 'Người thắng' : 'Bảng xếp hạng'}
          </h2>

          {isFinalized ? (
            event.winners?.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-2">
                {event.winners.map((w) => (
                  <li key={w.rank} className="flex items-center justify-between gap-2 text-sm">
                    <span className="ui-text flex min-w-0 items-center gap-1 truncate">
                      <span className="shrink-0">#{w.rank}</span>
                      {renderEntryName(w.displayName, w.userName, w.isAnonymous)}
                    </span>
                    <span className="ui-muted shrink-0">{formatCa(w.amount)} Ca</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ui-muted mt-2 text-sm">Sự kiện đã kết thúc, chưa có ai đạt.</p>
            )
          ) : event.leaderboard?.length > 0 ? (
            <ul className="mt-2 flex flex-col gap-2">
              {event.leaderboard.slice(0, 20).map((e) => (
                <li key={e.rank} className="flex items-center justify-between gap-2 text-sm">
                  <span className="ui-text flex min-w-0 items-center gap-1 truncate">
                    <span className="shrink-0">#{e.rank}</span>
                    {renderEntryName(e.displayName, e.userName, e.isAnonymous)}
                  </span>
                  <span className="ui-muted shrink-0">{formatCa(e.amount)} Ca</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ui-muted mt-2 text-sm">Chưa có ai donate trong sự kiện này.</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-6 w-full rounded-full bg-[#066bff] py-3 text-sm font-semibold text-white transition hover:brightness-105"
        >
          Donate cho creator bạn thích
        </button>
      </div>
    </div>
  );
};

export default DonateEventPage;
